import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGraphQL } from '../context/GraphQLProvider';
import Heading from '../components/common/Typography/Heading';
import Paragraph from '../components/common/Typography/Paragraph';
import MainButton from '../components/common/MainButton/MainButton';
import Alerts from '../components/common/Alerts/Alerts';
import { HandleCheckoutSuccessMutation } from '../services/CheckoutService';

const CheckoutSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const graphqlService = useGraphQL();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null); // 'success', 'failed', or null

  useEffect(() => {
    const verifySubscription = async () => {
      const sessionId = new URLSearchParams(location.search).get('session_id');
      
      if (!sessionId) {
        setVerificationStatus('failed');
        Alerts.error('Invalid session');
        return;
      }

      try {
        const response = await HandleCheckoutSuccessMutation(graphqlService, {
          sessionId
        });

        if (response?.handleCheckoutSuccess) {
          setVerificationStatus('success');
          Alerts.success('Subscription activated successfully!');
        } else {
          setVerificationStatus('failed');
          Alerts.error('Failed to verify subscription');
        }
      } catch (error) {
        console.error('Verification Error:', error);
        setVerificationStatus('failed');
        Alerts.error('Failed to verify subscription');
      } finally {
        setIsVerifying(false);
      }
    };

    verifySubscription();
  }, [location, navigate, graphqlService]);

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-2xl w-full mx-4 bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fi fi-rs-refresh text-2xl text-blue-500 animate-spin"></i>
            </div>
            <Heading variant="h4" className="mb-4 text-gray-800">Verifying your subscription...</Heading>
            <Paragraph variant="MainParagraph" className="text-gray-600">Please wait while we confirm your payment.</Paragraph>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-2xl w-full mx-4 bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fi fi-rs-x text-2xl text-red-500"></i>
            </div>
            <Heading variant="h4" className="mb-4 text-gray-800">Verification Failed</Heading>
            <Paragraph variant="MainParagraph" className="mb-6 text-gray-600">
              We couldn't verify your subscription. Please try again or contact support if the issue persists.
            </Paragraph>
            <div className="space-y-4">
              <MainButton
                variant="extralarge"
                onClick={() => navigate('/pricing')}
                className="w-full"
              >
                Return to Pricing
              </MainButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="max-w-2xl w-full mx-4 bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fi fi-rs-check text-2xl text-green-500"></i>
          </div>
          
          <Heading variant="h4" className="mb-4 text-gray-800">Thank You for Your Subscription!</Heading>
          
          <Paragraph variant="MainParagraph" className="mb-6 text-gray-600">
            Your subscription has been successfully activated.
            You now have access to all the features of your chosen plan.
          </Paragraph>

          <div className="space-y-4">
            <MainButton
              variant="extralarge"
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Go to Dashboard
            </MainButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess; 